import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

type ActivityType =
  | 'login'
  | 'page_view'
  | 'search'
  | 'monitoring_create'
  | 'monitoring_run'
  | 'monitoring_toggle'
  | 'monitoring_delete'
  | 'result_view'
  | 'cpv_search'
  | 'settings_update'
  | 'support_ticket'
  | 'alert_create'
  | 'alert_toggle'
  | 'export_pdf';

export function useTrackActivity() {
  const { user } = useAuth();

  const track = useCallback(
    async (activityType: ActivityType, metadata?: Record<string, unknown>) => {
      if (!user) return;
      try {
        await supabase.from('user_activities').insert([{
          user_id: user.id,
          activity_type: activityType,
          metadata: (metadata || {}) as Record<string, unknown>,
          page_path: window.location.pathname,
        }] as any);
      } catch {
        // silent fail – tracking should never break UX
      }
    },
    [user],
  );

  return track;
}

export function usePageViewTracker() {
  const { user } = useAuth();
  const location = useLocation();
  const lastTracked = useRef('');

  useEffect(() => {
    if (!user) return;
    const path = location.pathname;
    if (path === lastTracked.current) return;
    lastTracked.current = path;

    supabase
      .from('user_activities')
      .insert([{
        user_id: user.id,
        activity_type: 'page_view',
        page_path: path,
        metadata: {},
      }] as any)
      .then(() => {});
  }, [user, location.pathname]);
}
